import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Maximize2, ExternalLink, X } from "lucide-react";
import Layout, { PageHero } from "@/components/Layout";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { TACTILE_PAGE } from "@/content";

const DOC_PREVIEW = "https://docs.google.com/document/d/1zvViWgDkHEOMgf9haBxNLwYuea11EwOA-QDm2TXog_Q/preview";
const DOC_EDIT = "https://docs.google.com/document/d/1zvViWgDkHEOMgf9haBxNLwYuea11EwOA-QDm2TXog_Q/edit";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.45 } };

export default function TactileHealth() {
  const t = TACTILE_PAGE;
  const [docOpen, setDocOpen] = useState(false);
  return (
    <Layout>
      <PageHero crumb={<><Link to="/">Home</Link> / Tactile Health</>} title="Tactile Health" subtitle="AI-driven pain and touch diagnostics, and the patented Woodpecker device." />

      <section className="section">
        <div className="wrap split">
          <motion.div className="prose" {...fade}>
            <span className="eyebrow">About Tactile Health</span>
            <h2 style={{ marginTop: 14, color: "#fff" }}>A breakthrough in Fibromyalgia diagnosis</h2>
            <p style={{ color: "#fff" }}>{t.about}</p>
          </motion.div>
          <motion.div className="split-media" {...fade} transition={{ duration: 0.5, delay: 0.1 }}>
            <div className="media-card"><img src="/brand/pain-test.png" alt="Pain testing" style={{ background: "#fff" }} /></div>
          </motion.div>
        </div>
      </section>

      <section className="section soft">
        <div className="wrap">
          <span className="eyebrow">Our direction</span>
          <h2 className="h-sec">Mission, vision and goals</h2>
          <div className="two-col" style={{ marginTop: 34, gridTemplateColumns: "1fr 1fr 1fr" }}>
            {[["Mission", t.mission], ["Vision", t.vision], ["Goals", t.goals]].map(([k, v]) => (
              <div className="mvg-card" key={k}>
                <span className="k">{k}</span>
                <p>{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap split reverse">
          <motion.div className="split-media" {...fade}>
            <div
              className="media-card group"
              style={{ padding: 0, overflow: "hidden", position: "relative", cursor: "pointer" }}
              data-testid="woodpecker-doc-embed"
              onClick={() => setDocOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") setDocOpen(true); }}
            >
              <iframe
                src={DOC_PREVIEW}
                title="The Woodpecker document"
                style={{ width: "100%", height: "650px", border: "0", display: "block", background: "#fff", pointerEvents: "none" }}
                loading="lazy"
              />
              <div
                className="doc-expand-overlay"
                style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to top, rgba(10,22,40,0.35), rgba(10,22,40,0) 45%)",
                  display: "flex", alignItems: "flex-end", justifyContent: "center",
                  padding: "18px", opacity: 0, transition: "opacity 0.25s ease",
                }}
              >
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#0b2a4a", color: "#fff", fontWeight: 600,
                    padding: "10px 18px", borderRadius: 999, fontSize: 14,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                  }}
                >
                  <Maximize2 size={16} /> Click to expand &amp; read
                </span>
              </div>
            </div>
          </motion.div>
          <motion.div className="prose" {...fade} transition={{ duration: 0.5, delay: 0.1 }}>
            <span className="eyebrow">Our device</span>
            <h2 style={{ marginTop: 14, color: "#fff" }}>The Woodpecker</h2>
            <p style={{ color: "#fff" }}>{t.woodpecker}</p>
            <div className="doc-list">
              {t.patents.map((p) => (
                <a className="doc-item" href={p.url} target="_blank" rel="noreferrer" key={p.url}>
                  <div><b>{p.label}</b><br /><span>View the granted patent</span></div>
                </a>
              ))}
              <a className="doc-item" href={t.specSheet.url} target="_blank" rel="noreferrer" data-testid="woodpecker-spec-link">
                <div><b>{t.specSheet.label}</b><br /><span>Technical details and uses for the Woodpecker</span></div>
              </a>
              <a className="doc-item" href={t.robotDoc.url} target="_blank" rel="noreferrer" data-testid="robot-arm-doc-link">
                <div><b>{t.robotDoc.label}</b><br /><span>Full description of the robotic arm for mechanical pain disorders</span></div>
              </a>
              <a className="doc-item" href={t.encyclopedia.url} target="_blank" rel="noreferrer" data-testid="encyclopedia-link">
                <div><b>{t.encyclopedia.label}</b><br /><span>Our online reference on touch, body sense and pain</span></div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent
          className="p-0 overflow-hidden border-0 bg-white flex flex-col"
          style={{ maxWidth: "min(1100px, 96vw)", width: "96vw", height: "92vh" }}
          data-testid="woodpecker-doc-modal"
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#0b2a4a", color: "#fff" }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>The Woodpecker — Tactile Health</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <a
                href={DOC_EDIT}
                target="_blank"
                rel="noreferrer"
                data-testid="woodpecker-doc-open-newtab"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#fff", fontSize: 14, textDecoration: "none", opacity: 0.9 }}
              >
                <ExternalLink size={16} /> Open in Google Docs
              </a>
              <DialogClose asChild>
                <button data-testid="woodpecker-doc-close" aria-label="Close" style={{ display: "inline-flex", background: "transparent", border: 0, color: "#fff", cursor: "pointer" }}>
                  <X size={20} />
                </button>
              </DialogClose>
            </div>
          </div>
          <iframe
            src={DOC_PREVIEW}
            title="The Woodpecker document expanded"
            style={{ width: "100%", flex: 1, border: 0, background: "#fff" }}
          />
        </DialogContent>
      </Dialog>


      <section className="section soft">
        <div className="wrap two-col">
          <div className="card-panel prose">
            <span className="eyebrow">Pilot results</span>
            <h3 style={{ marginTop: 12 }}>Toward the first objective test for Fibromyalgia</h3>
            <p style={{ margin: 0 }}>{t.results}</p>
          </div>
          <div className="card-panel prose">
            <span className="eyebrow">Business model</span>
            <h3 style={{ marginTop: 12 }}>Software as a Service</h3>
            <p style={{ margin: 0 }}>{t.saas}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
